import React, { useContext, useEffect, useState, useCallback } from 'react';
import { css, styled, keyframes } from '@compiled/react';
import memoize from 'memoize-one';
import { useIntl } from 'react-intl-next';
import type { MutationUpdaterFn } from 'apollo-client';
import type { DataProxy } from 'apollo-cache';

import { token } from '@atlaskit/tokens';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Text, xcss, Box } from '@atlaskit/primitives';

import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { CommentRenderer } from '@confluence/comment/entry-points/CommentRenderer';
import { scrollCommentIntoView } from '@confluence/comments-util/entry-points/domUtils';
import {
	checkElementExpandInEditor,
	scrollToElementInEditor,
} from '@confluence/comments-util/entry-points/editorAnnotations';
import type {
	CommentInlineResolveProperties,
	ReactionsSummary,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery';
import type {
	SitePermissionType,
	InlineCommentQueryInlineResolveProperties,
} from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery';
import { fg } from '@confluence/feature-gating';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData';
import { useContentType } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import type {
	ActiveCommentInlineResolveProperties,
	FooterResolvedProperties,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo';
import { getUnreadCommentsIntersectionObserver } from '@confluence/unread-comments/entry-points/UnreadCommentsIntersectionObserver';
import { useViewedComments } from '@confluence/unread-comments/entry-points/useViewedComments';
import {
	isValidUnreadComment,
	buildValidationObjectFromProps,
} from '@confluence/unread-comments/entry-points/unreadCommentsUtils';

import type { CommentPermissions, InlineCommentsMode, CommentAction } from './inlineCommentsTypes';
import { i18n } from './inlineCommentsi18n';
import { CommentActions } from './CommentActions';
import { DanglingContent } from './DanglingContent';
import { CommentAuthor } from './CommentAuthor';
import { HighlightLineSVG } from './assets/HighlightLineSVG';
import { WithCurvedBranchingStyle } from './WithCurvedBranchingStyle';
import { useCommentActions } from './hooks/useCommentActions';
import { useResizeObserver } from './hooks/useResizeObserver';

type CommentContainerProps = {
	isFocused?: boolean;
	isRemoving?: boolean;
	mode: InlineCommentsMode;
	isCommentsPanel?: boolean;
	isFabricPage?: boolean;
};

const fadeOut = keyframes({
	from: { opacity: 1 },
	to: { opacity: 0.5 },
});

const unreadIconStyle = css({ paddingTop: token('space.050') });

const unreadStyleInCommentsPanel = css({
	position: 'relative',
	top: token('space.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentContainer = styled.div<CommentContainerProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	`
  display: flex;
  flex-direction: column;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
	padding: ${(props: CommentContainerProps) => {
		return props.mode === 'view-all'
			? token('space.400')
			: props.isCommentsPanel === true
				? 'inherit'
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
					props.isFabricPage && fg('confluence_frontend_content_wrapper')
					? `${token('space.150')} ${token('space.200')} ${token('space.0')} ${token('space.200')}`
					: `${token('space.150')} ${token('space.200')}`;
	}};
  border-left: 0;
  border-top: 0;
  animation-duration: 0.5s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  ${(props: CommentContainerProps) => Boolean(props.isRemoving) && `animation: ${fadeOut};`}
  animation-timing-function: linear;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  cursor: ${(props: CommentContainerProps) =>
		Boolean(props.isRemoving) ? 'not-allowed' : 'inherit'};
  outline: none;

${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  background: ${(props: CommentContainerProps) =>
		Boolean(props.isFocused && !props.isCommentsPanel)
			? `${token('color.background.selected')}`
			: undefined};

  blockquote {
    color: ${token('color.text.subtlest')};
  }

  &:last-of-type {
    border-bottom: 0;
  }
`,
);

const withLeftBorderStyle = css({
	borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	paddingBottom: token('space.100'),
});

const commentBodyStyles = css({
	marginTop: token('space.150'),
	marginLeft: token('space.200'),
	paddingLeft: token('space.300'),
	paddingRight: token('space.050'),
});

const commentReplyStyles = css({
	marginLeft: token('space.0'),
	paddingLeft: token('space.0'),
});

const commentBodyReplyStyles = css({
	marginTop: token('space.100'),
});

const commentBodyNestedReplyStyle = css({
	marginLeft: token('space.100'),
});

const warningMessageStyle = xcss({
	display: 'flex',
	fontStyle: 'italic',
	padding: 'space.150',
});

const xcssTranslucentStyle = xcss({
	opacity: '50%',
});

const cssTranslucentStyle = css({
	opacity: '50%',
});

const annotatedTextBodyStyle = xcss({
	borderWidth: '1.5px',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	padding: 'space.200',
	width: '100%',
	backgroundColor: 'elevation.surface',
	marginBottom: 'space.150',
});

const danglingAnnotatedTextBodyStyle = xcss({
	borderWidth: '1.5px',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
	width: '100%',
	backgroundColor: 'elevation.surface',
	marginBottom: 'space.150',
});

const annotatedTextBodyHoverStyle = xcss({
	cursor: 'pointer',
});

const annotatedTextBodyWithPressedStyle = xcss({
	background: token('elevation.surface.pressed'),
});

// This snippet is being used in RepliesOpener.tsx
const dotStyles = xcss({
	height: token('space.100'),
	width: token('space.100'),
	backgroundColor: 'color.background.brand.bold',
	borderRadius: 'radius.full',
	margin: 'space.050',
});

const dotBaseStyles = xcss({
	height: token('space.100'),
	width: token('space.100'),
	backgroundColor: 'color.background.brand.bold',
	borderRadius: 'radius.full',
	margin: 'space.050',
	transition: 'opacity 300ms ease-in-out',
});

const dotVisibleStyles = xcss({
	opacity: 1,
});

const dotHiddenStyles = xcss({
	opacity: 0,
});

const replyBranchStyles = css({
	position: 'relative',
	// for the curvy border
	'&::before': {
		content: "''",
		position: 'absolute',
		left: '-24px',
		top: '16px',
		height: `var(--replyBranchHeight)`,
		bottom: '30px',
		borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		paddingLeft: token('space.200'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RelativeContainer = styled.div({
	position: 'relative',
	height: '1px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginTop: '-1px', // Needs to offset height to prevent jumping when we switch to edit mode
});

const highlightSvgBoxStyles = xcss({
	height: '22px',
	width: '2px',
});

const footerCommentAuthorStyles = xcss({
	paddingTop: 'space.100',
});

type CommentBodyProps = {
	pageId: string;
	pageType: string;
	avatarUrl?: string;
	commentId: string;
	content: string;
	date: string;
	displayName?: string | null;
	permissions: CommentPermissions;
	dateUrl?: string;
	createdAt?: string;
	createdAtNonLocalized?: string;
	versionByDisplayName?: string | null;
	versionDate: string;
	userId?: string | null;
	deleteComment?: () => void;
	editComment?: () => void;
	resolveComment?: () => void;
	isReply: boolean;
	isEdited: boolean;
	parentCommentId?: string | null;
	isFocused?: boolean;
	shouldScrollIntoView?: boolean;
	isRemoving?: boolean;
	numReplies?: number;
	isFabricPage?: boolean;
	isCommentActive?: boolean;
	isCurrentUserAnonymous?: boolean;
	mode: InlineCommentsMode;
	onRendered?: () => void;
	onClose?: () => void;
	shouldAutofocus?: boolean;
	onAutoFocused?: () => void;
	permissionType?: SitePermissionType;
	maxHeight?: number;
	fadeOutHeight?: number;
	onComplete?: () => void;
	supportedActions?: CommentAction[];
	isUnread?: boolean;
	unreadCommentRef?: React.RefObject<HTMLDivElement>;
	isCommentsPanel?: boolean;
	isHovered?: boolean;
	isResolvedByAnotherUser?: boolean;
	resolveProperties?:
		| CommentInlineResolveProperties
		| FooterResolvedProperties
		| ActiveCommentInlineResolveProperties
		| InlineCommentQueryInlineResolveProperties;
	annotationId?: string;
	annotatedText?: string | null;
	reopenComment?: () => void;
	replyToComment?: (commentId: string) => void;
	inheritedReactionsData?: ReactionsSummary | null;
	reactionsCacheUpdateFn?: (
		emojiId: string,
		actionType: 'add' | 'delete',
		contentId: string,
		containerId?: string,
		cache?: DataProxy,
	) => MutationUpdaterFn<any> | void;
	isDeletedByAnotherUser?: boolean;
	currentlySelectedThreadKey?: string;
	setCurrentlySelectedThreadKey?: (markerRef: string) => void;
	isAnnotatedTextPressed?: boolean;
	setAnnotatedTextPressed?: (state: boolean) => void;
	commentType: CommentType;
	adjustForNestedReplies?: boolean;
	hideBranchingStyle?: boolean;
	canAddComments?: boolean;
	isThreadHovered?: boolean;
	isParentCommentOpen?: boolean;
	isStandalonehovered?: boolean;
	isFooterComment?: boolean;
	adjustForFooterNestedReplies?: boolean;
	isStandaloneComment?: boolean;
	isOffline?: boolean;
	showRepliesWithCurvedBorders?: boolean;
	replyHasNestedObjects?: boolean;
};

// Parse content to JSON or Fail view inline experience and return null
const parseContent = memoize((content, experienceTracker) => {
	if (!content?.length) return null;

	try {
		return JSON.parse(content);
	} catch (error) {
		experienceTracker.stopOnError({
			name: VIEW_INLINE_COMMENT_EXPERIENCE,
			error,
			attributes: {
				stopPoint: 'inline-comments-common/CommentBody',
			},
		});
		return null;
	}
});

const STABLE_SUPPORTED_ACTIONS_DEFAULT: CommentAction[] = ['edit', 'delete', 'resolve'];

export const CommentBody = ({
	pageId,
	pageType,
	avatarUrl,
	commentId,
	content,
	date,
	createdAt,
	createdAtNonLocalized,
	versionByDisplayName,
	versionDate,
	displayName,
	permissions,
	dateUrl,
	userId,
	deleteComment,
	editComment,
	resolveComment,
	isReply,
	isEdited,
	parentCommentId,
	isFocused,
	shouldScrollIntoView,
	isRemoving,
	numReplies,
	isFabricPage,
	isCommentActive,
	isCurrentUserAnonymous,
	mode,
	onRendered,
	onClose,
	shouldAutofocus,
	onAutoFocused,
	permissionType,
	maxHeight,
	fadeOutHeight,
	onComplete,
	supportedActions = STABLE_SUPPORTED_ACTIONS_DEFAULT,
	isUnread = false,
	unreadCommentRef,
	isCommentsPanel = false,
	isHovered,
	isResolvedByAnotherUser = false,
	resolveProperties,
	annotationId,
	annotatedText,
	reopenComment,
	replyToComment,
	inheritedReactionsData,
	reactionsCacheUpdateFn,
	isDeletedByAnotherUser,
	currentlySelectedThreadKey,
	setCurrentlySelectedThreadKey,
	isAnnotatedTextPressed,
	setAnnotatedTextPressed,
	commentType = CommentType.INLINE,
	adjustForNestedReplies = false,
	hideBranchingStyle = false,
	canAddComments = true,
	isThreadHovered,
	isParentCommentOpen,
	isStandalonehovered,
	isFooterComment = false,
	adjustForFooterNestedReplies = false,
	isStandaloneComment = false,
	isOffline = false,
	showRepliesWithCurvedBorders,
	replyHasNestedObjects = false,
}: CommentBodyProps) => {
	const { formatMessage } = useIntl();
	const [showingCopyLinkButton, setShowingCopyLink] = useState(false);
	const [hasFallbackContent, setHasFallbackContent] = useState(false);
	const [overflowPortal, setOverflowPortal] = useState<HTMLDivElement | undefined>(undefined);
	const [isAnnotatedTextHovered, setIsAnnotatedTextHovered] = useState(false);
	const pageMode = useGetPageMode();
	const eventEmitter =
		pageMode === PageMode.EDIT || pageMode === PageMode.LIVE
			? getEditorAnnotationEventEmitter()
			: getRendererAnnotationEventEmitter();

	const isSelected = currentlySelectedThreadKey === annotationId && !isStandalonehovered;
	const [contentType] = useContentType();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const [containerRef, replyBranchHeight] = useResizeObserver();

	// Track the currently observed element for cleanup
	const [observedElement, setObservedElement] = useState<HTMLElement | null>(null);

	const { getViewedCommentsCacheOnly } = useViewedComments({ pageId: pageId || '' });
	const { userId: currentUserId } = useSessionData();

	// Create a ref callback to set the ref and observe when the DOM element is available
	const handleUnreadCommentRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (!fg('confluence_frontend_unread_comments_fixes')) {
				return;
			}

			// isUnread comes from unreadCommentIDSet and doesn't contain dangling and resolved, we're only using that for the unread indicators
			// for observing unread comments, we'll rely on the viewedComments set as the source of truth
			const viewedCommentsResult = getViewedCommentsCacheOnly();
			const viewedComments =
				viewedCommentsResult.success && viewedCommentsResult.data
					? viewedCommentsResult.data
					: new Set<string>();

			const commentForValidation = buildValidationObjectFromProps({
				commentId,
				userId,
				createdAt,
				commentType: commentType === CommentType.INLINE ? 'INLINE' : 'FOOTER',
				resolveProperties: { resolved: false }, // overriding resolveProperties since we still want to observe resolved comments for unread
			});

			const isValidUnread = isValidUnreadComment({
				comment: commentForValidation,
				currentUserId: currentUserId || '',
				viewedComments,
			});

			const shouldObserve = node && isValidUnread;
			if (shouldObserve) {
				const observer = getUnreadCommentsIntersectionObserver();
				observer.observeComment(node);
				setObservedElement(node);
			} else {
				// If we shouldn't observe this node, clear the observed element
				setObservedElement(null);
			}
		},
		[commentId, userId, createdAt, commentType, getViewedCommentsCacheOnly, currentUserId],
	);

	const { pageInfo } = usePageInfo();
	const isArchived = pageInfo?.status === 'archived';

	const { mainCommentActions, hoveredActionItems } = useCommentActions({
		pageId,
		pageType,
		commentId,
		permissions,
		isReply,
		parentCommentId,
		deleteComment,
		editComment,
		resolveComment,
		restrictEdit: hasFallbackContent,
		restrictDelete: Boolean(numReplies),
		numReplies,
		mode,
		commentDateUrl: dateUrl,
		overflowMenuPortal: overflowPortal,
		supportedActions,
		isCommentsPanel,
		isHovered,
		annotationId,
		resolveProperties: resolveProperties as
			| CommentInlineResolveProperties
			| FooterResolvedProperties,
		reopenComment,
		replyToComment,
		inheritedReactionsData,
		reactionsCacheUpdateFn,
		isResolvedByAnotherUser,
		commentType,
		hideBranchingStyle,
		canAddComments,
		isDeletedByAnotherUser,
		isSelected,
		isParentCommentOpen,
		isFooterComment,
		isStandaloneComment,
		isOffline,
		isFabricPage,
		isArchived,
		authorName: displayName,
	});

	useEffect(() => {
		/* We only need to scroll for focused replies as parent comments will have scrolled when selected */
		if (
			shouldScrollIntoView &&
			isFocused &&
			containerRef &&
			containerRef.current &&
			!fg('confluence_comments_unified_navigation')
		) {
			scrollCommentIntoView({ commentElement: containerRef.current, isFabricPage });
		}
	}, [containerRef, isFocused, shouldScrollIntoView, isFabricPage]);

	useEffect(() => {
		if (shouldAutofocus) {
			containerRef.current &&
				containerRef.current.focus({
					preventScroll: fg('confluence_comment_prevent_focus_autoscroll'),
				});
			onAutoFocused && onAutoFocused();
		}
	}, [containerRef, shouldAutofocus, onAutoFocused]);

	// clean up the observer when the component unmounts
	useEffect(() => {
		return () => {
			if (observedElement && fg('confluence_frontend_unread_comments_fixes')) {
				const observer = getUnreadCommentsIntersectionObserver();
				observer.unobserveComment(observedElement);
			}
		};
	}, [observedElement]);

	const adf = parseContent(content, experienceTracker);

	const onCommentContentFallback = useCallback(() => setHasFallbackContent(true), []);

	const handleRef = (portalDiv: HTMLDivElement) => {
		/* do a check to prevent from re-running setPopupPortal multiple times*/
		if (!overflowPortal && portalDiv) {
			setOverflowPortal(portalDiv);
		}
	};

	const handleMouseEventOnWrapper = (eventType: 'enter' | 'leave') => () => {
		setShowingCopyLink(eventType === 'enter');
	};

	const commentRemovedByAnotherUser = isDeletedByAnotherUser || isResolvedByAnotherUser;
	const shouldShowOverflowMenu = supportedActions.length > 0;

	const showDefaultReplyStyle =
		showRepliesWithCurvedBorders ??
		(isCommentsPanel && isReply && fg('confluence_frontend_comments_panel_v2'));

	const getHighlightBackgroundColor = () => {
		if (isAnnotatedTextPressed) {
			return token('color.background.accent.yellow.subtle.hovered');
		}
		if (isSelected) {
			return token('color.background.accent.yellow.subtle');
		}
		if (
			isAnnotatedTextHovered ||
			(isThreadHovered && fg('confluence_frontend_comments_panel_v2'))
		) {
			return token('color.background.accent.yellow.subtlest.hovered');
		}
		return 'inherit';
	};

	// Duplicate method, we already have the same in commentThreadHelper
	// this will be removed later

	const handleMouseUp = () => {
		if (annotationId && setCurrentlySelectedThreadKey) {
			if (!fg('confluence_comments_unified_navigation')) {
				setCurrentlySelectedThreadKey(annotationId);
				const commentElement = document.getElementById(annotationId);
				if (commentElement && isParentCommentOpen) {
					if (pageMode === PageMode.EDIT || pageMode === PageMode.LIVE) {
						scrollToElementInEditor({ targetElement: commentElement, viewingRoomOffset: -300 });
						checkElementExpandInEditor(commentElement);
						eventEmitter.emit('setselectedannotation', annotationId);
					} else {
						eventEmitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, {
							annotationId,
						});
						scrollCommentIntoView({
							commentElement,
							isFabricPage: true,
							useInstantScroll: false,
							isAbove: false,
							commentThreadAnnotationId: annotationId,
							isCommentsPanel,
						});
					}
				}
			}
			setAnnotatedTextPressed && setAnnotatedTextPressed(false);
		}
	};

	const handleMouseEnter = (): void => {
		if (!fg('confluence_frontend_comments_panel_v2')) {
			if (annotationId) {
				if (pageMode === PageMode.EDIT || pageMode === PageMode.LIVE) {
					eventEmitter.emit('sethoveredannotation', annotationId);
				} else {
					eventEmitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_HOVERED, {
						annotationId,
					});
				}
				setIsAnnotatedTextHovered(true);
			}
		}
	};

	const handleMouseLeave = (): void => {
		if (!fg('confluence_frontend_comments_panel_v2')) {
			if (annotationId) {
				if (pageMode === PageMode.EDIT || pageMode === PageMode.LIVE) {
					eventEmitter.emit('removehoveredannotation', annotationId);
				} else {
					eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_HOVERED, {
						annotationId,
					});
				}
				setIsAnnotatedTextHovered(false);
			}
		}
	};

	const shouldShowCommentContentContext = annotatedText && isCommentsPanel && !isReply;

	const showContentUnavailableForDanglingComment =
		(resolveProperties as CommentInlineResolveProperties)?.isDangling &&
		fg('confluence_frontend_content_wrapper');

	return (
		<CommentContainer
			ref={containerRef}
			data-comment-id={commentId}
			data-testid={commentId}
			isFocused={isFocused}
			isRemoving={isRemoving}
			mode={mode}
			isCommentsPanel={isCommentsPanel}
			role="article"
			tabIndex={-1}
			onMouseEnter={handleMouseEventOnWrapper('enter')}
			onMouseLeave={handleMouseEventOnWrapper('leave')}
			css={{ position: 'relative ' }}
			isFabricPage={isFabricPage}
		>
			<div
				ref={
					fg('confluence_frontend_unread_comments_fixes')
						? handleUnreadCommentRef
						: unreadCommentRef
				}
				data-comment-id={commentId}
			>
				{mode !== 'view' && (
					<RelativeContainer>
						<div data-testid="overflow-container" ref={handleRef} />
					</RelativeContainer>
				)}
				<div
					css={
						isReply &&
						isCommentsPanel &&
						!hideBranchingStyle &&
						fg('confluence_frontend_comments_panel_v2') &&
						replyBranchStyles
					} // We need to adjust the size of the border if we have a nested reply scroller below this
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={
						!hideBranchingStyle
							? ({
									'--replyBranchHeight': `${replyBranchHeight + (adjustForNestedReplies ? 53 : 0)}px`,
								} as React.CSSProperties)
							: {}
					}
				>
					<WithCurvedBranchingStyle
						showDefaultStyles={showDefaultReplyStyle}
						customStyles={{
							...(isCommentsPanel
								? {
										height: fg('confluence_frontend_content_wrapper') ? '28px' : '20px',
									}
								: {}), // Have to adjust for padding on author (vs reply thread parent) with flag on
							...(isFooterComment ? { height: '25px' } : {}), // Accounts for additional padding added to the author
							...(adjustForFooterNestedReplies ? { left: '-25px' } : {}), // Accounts for the left border of the parent comment
						}}
					>
						<Box
							xcss={
								isFooterComment ||
								(isCommentsPanel && isReply && fg('confluence_frontend_content_wrapper'))
									? footerCommentAuthorStyles
									: undefined
							}
						>
							<Inline spread="space-between">
								<CommentAuthor
									commentMode="view"
									userId={userId}
									date={date}
									createdAtNonLocalized={createdAtNonLocalized}
									versionByDisplayName={versionByDisplayName}
									versionDate={versionDate}
									displayName={displayName}
									avatarUrl={avatarUrl}
									commentDateUrl={dateUrl}
									commentId={isCurrentUserAnonymous ? commentId : null}
									permissionType={permissionType}
									isInactiveComment={Boolean(mode === 'view-all' && !isCommentActive)}
									showCopyLink={showingCopyLinkButton}
									resolveProperties={
										resolveProperties as CommentInlineResolveProperties | FooterResolvedProperties
									}
									isCommentsPanel={isCommentsPanel}
									isGeneralComments={commentType === CommentType.GENERAL}
									isReply={isReply}
									isEdited={isEdited}
									isFooterComment={isFooterComment}
									isStandaloneComment={isStandaloneComment}
								/>

								{fg('confluence_frontend_unread_comments_fixes')
									? !isHovered && (
											<div
												css={[
													unreadIconStyle,
													isCommentsPanel &&
														fg('confluence_frontend_comments_panel_v2') &&
														unreadStyleInCommentsPanel,
												]}
												data-testid={`unread-comment-indicator-${commentId}`}
												aria-hidden="true"
											>
												<Box
													xcss={[dotBaseStyles, isUnread ? dotVisibleStyles : dotHiddenStyles]}
												/>
											</div>
										)
									: isUnread &&
										!isHovered && (
											<div
												css={[
													unreadIconStyle,
													isCommentsPanel &&
														fg('confluence_frontend_comments_panel_v2') &&
														unreadStyleInCommentsPanel,
												]}
												data-testid={`unread-comment-indicator-${commentId}`}
											>
												<Box xcss={dotStyles} />
											</div>
										)}
							</Inline>
						</Box>
					</WithCurvedBranchingStyle>
				</div>
				{/* TODO - COMMENTS-4934: clean up 'confluence_frontend_comments_a11y_kb' FG */}
				{shouldShowOverflowMenu && fg('confluence_frontend_comments_a11y_kb') && hoveredActionItems}
				<div
					css={[
						isCommentsPanel && fg('confluence_frontend_comments_panel_v2') && commentBodyStyles,
						isCommentsPanel &&
							isReply &&
							fg('confluence_frontend_comments_panel_v2') &&
							commentReplyStyles,
						isCommentsPanel &&
							!isReply &&
							!hideBranchingStyle &&
							(numReplies !== 0 || canAddComments) &&
							fg('confluence_frontend_comments_panel_v2') &&
							withLeftBorderStyle,
						isFooterComment && isReply && commentBodyReplyStyles,
						isFooterComment && isReply && replyHasNestedObjects && commentBodyNestedReplyStyle,
						isStandaloneComment && isReply && commentBodyReplyStyles,
						isCommentsPanel &&
							isReply &&
							fg('confluence_frontend_content_wrapper') &&
							commentBodyReplyStyles,
					]}
				>
					{shouldShowCommentContentContext && (
						<>
							{resolveProperties?.resolved &&
								!fg('confluence_frontend_comments_panel_v2') &&
								annotatedText && (
									<Inline
										space="space.150"
										alignBlock="center"
										xcss={[isResolvedByAnotherUser && xcssTranslucentStyle]}
										testId="highlight-text"
									>
										<Box xcss={highlightSvgBoxStyles}>
											<HighlightLineSVG />
										</Box>
										<Text color="color.text.subtle" size="medium" maxLines={1}>
											{annotatedText}
										</Text>
									</Inline>
								)}
							{contentType !== 'database' && fg('confluence_frontend_comments_panel_v2') && (
								<Inline
									space="space.150"
									alignBlock="center"
									xcss={[
										isResolvedByAnotherUser &&
											!fg('confluence_frontend_content_wrapper') &&
											xcssTranslucentStyle,
									]}
									testId="highlight-text"
								>
									<Box
										data-testId="annotated-section-for-inlineComment"
										xcss={[
											showContentUnavailableForDanglingComment &&
											fg('confluence_frontend_new_dangling_comments_ux')
												? danglingAnnotatedTextBodyStyle
												: annotatedTextBodyStyle,
											!isResolvedByAnotherUser &&
												isAnnotatedTextHovered &&
												!isAnnotatedTextPressed &&
												!showContentUnavailableForDanglingComment &&
												annotatedTextBodyHoverStyle,
											!isResolvedByAnotherUser &&
												isAnnotatedTextPressed &&
												!showContentUnavailableForDanglingComment &&
												annotatedTextBodyWithPressedStyle,
										]}
										onMouseEnter={handleMouseEnter}
										onMouseLeave={handleMouseLeave}
										onMouseDown={() => {
											if (annotationId) {
												setAnnotatedTextPressed && setAnnotatedTextPressed(true);
											}
										}}
										onMouseUp={handleMouseUp}
									>
										{showContentUnavailableForDanglingComment ? (
											<DanglingContent annotatedText={annotatedText} />
										) : (
											annotatedText && (
												<Text size="small" maxLines={3}>
													<span
														style={{
															background: getHighlightBackgroundColor(),
															borderBottom:
																isAnnotatedTextPressed ||
																isSelected ||
																isAnnotatedTextHovered ||
																(isThreadHovered && fg('confluence_frontend_comments_panel_v2'))
																	? `2px solid ${token('color.icon.accent.yellow')}`
																	: '',
														}}
													>
														{annotatedText}
													</span>
												</Text>
											)
										)}
									</Box>
								</Inline>
							)}
						</>
					)}
					{adf && (
						<>
							<div
								css={[
									isCommentsPanel &&
										commentRemovedByAnotherUser &&
										!fg('confluence_frontend_content_wrapper') &&
										cssTranslucentStyle,
								]}
							>
								<CommentRenderer
									commentId={commentId}
									adf={adf}
									onContentFallback={onCommentContentFallback}
									isTruncatedContent={mode === 'view-all' && !isCommentActive}
									onRendered={onRendered}
									onComplete={onComplete}
									maxHeight={maxHeight}
									fadeOutHeight={fadeOutHeight}
									isInlineComment
									isCommentsPanel={isCommentsPanel}
									isEdited={isEdited}
									versionByDisplayName={versionByDisplayName}
									versionDate={versionDate}
									commentType={commentType}
								/>
							</div>
							{isDeletedByAnotherUser && (
								<Box xcss={warningMessageStyle}>
									<Text weight="bold">{formatMessage(i18n.deletedCommentActionLabel)}</Text>
								</Box>
							)}
						</>
					)}
				</div>
				{/* TODO - COMMENTS-4934: clean up 'confluence_frontend_comments_a11y_kb' FG */}
				{shouldShowOverflowMenu &&
					isFabricPage &&
					fg('confluence_frontend_comments_a11y_kb') &&
					mainCommentActions}
				{shouldShowOverflowMenu &&
					(!isFabricPage || !fg('confluence_frontend_comments_a11y_kb')) && (
						<CommentActions
							pageId={pageId}
							pageType={pageType}
							commentId={commentId}
							permissions={permissions}
							isReply={isReply}
							parentCommentId={parentCommentId}
							deleteComment={deleteComment}
							editComment={editComment}
							resolveComment={resolveComment}
							restrictEdit={hasFallbackContent}
							restrictDelete={Boolean(numReplies)}
							numReplies={numReplies}
							isCommentActive={isCommentActive}
							mode={mode}
							commentDateUrl={dateUrl}
							onClose={onClose}
							overflowMenuPortal={overflowPortal}
							supportedActions={supportedActions}
							isCommentsPanel={isCommentsPanel}
							isHovered={isHovered}
							annotationId={annotationId}
							resolveProperties={
								resolveProperties as CommentInlineResolveProperties | FooterResolvedProperties
							}
							reopenComment={reopenComment}
							replyToComment={replyToComment}
							isUnread={isUnread}
							inheritedReactionsData={inheritedReactionsData}
							reactionsCacheUpdateFn={reactionsCacheUpdateFn}
							isResolvedByAnotherUser={isResolvedByAnotherUser}
							commentType={commentType}
							hideBranchingStyle={hideBranchingStyle}
							canAddComments={canAddComments}
							isDeletedByAnotherUser={isDeletedByAnotherUser}
							isSelected={isSelected}
							isParentCommentOpen={isParentCommentOpen}
							isFooterComment={isFooterComment}
							isStandaloneComment={isStandaloneComment}
							isOffline={isOffline}
							isFabricPage={isFabricPage}
							isArchived={isArchived}
							authorName={displayName}
						/>
					)}
			</div>
		</CommentContainer>
	);
};
